import React, { useState, useEffect } from 'react';
import { Controller } from 'react-hook-form-new';
import {
  Button,
  Checkbox,
  Divider,
  FormControlLabel,
  Icon,
  IconButton,
  Icons,
  SimpleDialog,
  Stack,
  TextArea,
  Typography,
} from '@nextiva/next-ui-components';
import { IEnablementInfoV3 } from 'interface/WizardV3';
import { StyledIcon } from '../Steps.styled';
import { useWizardContextV3 } from '../WizardContextV3/WizardContextV3';
import {
  DEFAULT_NUM_SAMPLE_MESSAGES,
  EMPTY_FIELD_VALUE,
  FIELD_NAMES,
  MESSAGE_CONTENT_FIELDS,
} from '../constants';
import { UploadComponent } from '../FileUpload';
import { FileTypesDialogMms } from '../FileTypesDialog';
import { Body2Typo, OverlineTypo, SubtitleTypo } from '../WizardV3.styled';
import { LabelWithDeleteButton } from './LabelWithDeleteButton';

const dataTestId = 'PlatformAdminUI-MessageContent';
type SampleTypes = 'sampleMessage3' | 'sampleMessage4' | 'sampleMessage5';

export function MessageContent() {
  const {
    control,
    enablementInfo,
    getValues,
    setValue,
    trigger,
    filesMMS,
    formState: { errors },
  } = useWizardContextV3();
  const [openModal, setOpenModal] = useState(false);
  const [samples, setSamples] = useState(DEFAULT_NUM_SAMPLE_MESSAGES);
  const [typeModal, setTypeModal] = useState(false);

  function shiftSampleMessages(sampleMessages: string[]) {
    const extraSampleMessages = sampleMessages.filter((message) => !!message);
    setValue('sampleMessage3', extraSampleMessages?.[0] || EMPTY_FIELD_VALUE);
    setValue('sampleMessage4', extraSampleMessages?.[1] || EMPTY_FIELD_VALUE);
    setValue('sampleMessage5', extraSampleMessages?.[2] || EMPTY_FIELD_VALUE);
    /**
     * This is somewhat of a hack to get around a limitation in react-hook-form v7.12.0.
     *
     * There is an issue in react-hook-form that prevents the form from submitting in this scenario:
     * If the app loads with an invalid sampleMessage3 from the server, react-hook-form will render sampleMessage3, but it will be in an invalid state.
     * If we delete that sampleMessage3 from the UI by unmounting it, setting it to EMPTY_FIELD_VALUE, and unregistering the field in react-hook-form,
     * react-hook-form will STILL be in an invalid state and still prevent the form from submitting.
     * To get around it, if manually force the validation on a field that is still visible in the UI, in this case trigger('sampleMessageOne'),
     * react-hook-form will then recognize the removed sampleMessage3 and will allow the form to submit.
     *
     * This issue does not exist in the latest version react-hook-form, v7.52.0, but for now we are limited to v7.12.0 till we upgrade.
     */
    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    trigger('sampleMessageOne');

    return extraSampleMessages;
  }

  function handleDeleteSampleMessage(
    sampleMessageId: keyof Pick<
      IEnablementInfoV3,
      'sampleMessage3' | 'sampleMessage4' | 'sampleMessage5'
    >,
  ) {
    setSamples((sam) => sam - 1);
    setValue(sampleMessageId, EMPTY_FIELD_VALUE);
    shiftSampleMessages([
      getValues('sampleMessage3'),
      getValues('sampleMessage4'),
      getValues('sampleMessage5'),
    ]);
  }

  const sampleMessage3 = enablementInfo?.sampleMessage3;
  const sampleMessage4 = enablementInfo?.sampleMessage4;
  const sampleMessage5 = enablementInfo?.sampleMessage5;

  useEffect(() => {
    const shiftedSampleMessages = shiftSampleMessages([
      sampleMessage3,
      sampleMessage4,
      sampleMessage5,
    ]);

    setSamples(DEFAULT_NUM_SAMPLE_MESSAGES + shiftedSampleMessages.length);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sampleMessage3, sampleMessage4, sampleMessage5]);

  return (
    <div>
      <Stack mb={6}>
        <SubtitleTypo mb={2}>
          Choose the types of content that will be included in your outgoing
          messages
        </SubtitleTypo>
        <Controller
          control={control}
          name={MESSAGE_CONTENT_FIELDS.EMBEDDED_LINK}
          render={({ field: { onChange, value } }) => (
            <FormControlLabel
              sx={{ mt: '16px' }}
              value={value}
              control={
                <Checkbox
                  checked={!!value}
                  disableRipple
                  data-testid={`${dataTestId}-Embedded-Links`}
                  onChange={(e) => onChange(e.target.checked)}
                />
              }
              label={
                <Body2Typo ml={2}>
                  <b>{FIELD_NAMES[MESSAGE_CONTENT_FIELDS.EMBEDDED_LINK]}</b>:
                  Indicates whether messages include embedded links of any kind.
                  Note that public URL shorteners (bitly, tinyurl) are not
                  accepted.
                </Body2Typo>
              }
            />
          )}
        />
        <Controller
          control={control}
          name={MESSAGE_CONTENT_FIELDS.EMBEDDED_PHONE}
          render={({ field: { onChange, value } }) => (
            <FormControlLabel
              sx={{ mt: '16px' }}
              value={value}
              control={
                <Checkbox
                  checked={!!value}
                  disableRipple
                  data-testid={`${dataTestId}-Embedded-PhoneNumbers`}
                  onChange={(e) => onChange(e.target.checked)}
                />
              }
              label={
                <Body2Typo ml={2}>
                  <b>{FIELD_NAMES[MESSAGE_CONTENT_FIELDS.EMBEDDED_PHONE]}</b>:
                  Indicates whether messages include embedded phone numbers
                  (except the required HELP information contact phone number).
                </Body2Typo>
              }
            />
          )}
        />
        <Controller
          control={control}
          name={MESSAGE_CONTENT_FIELDS.DIRECT_LENDING}
          render={({ field: { onChange, value } }) => (
            <FormControlLabel
              sx={{ mt: '16px' }}
              value={value}
              control={
                <Checkbox
                  checked={!!value}
                  disableRipple
                  data-testid={`${dataTestId}-Direct-Lending`}
                  onChange={(e) => onChange(e.target.checked)}
                />
              }
              label={
                <Body2Typo ml={2}>
                  <b>
                    {FIELD_NAMES[MESSAGE_CONTENT_FIELDS.DIRECT_LENDING]} or Loan
                    Arrangement
                  </b>
                  : Indicates whether messages include agreements of loans or
                  financing (including arranging third-party financing, loans,
                  or mortgages) of any kind.
                </Body2Typo>
              }
            />
          )}
        />
        <Controller
          control={control}
          name={MESSAGE_CONTENT_FIELDS.AGE_GATED}
          render={({ field: { onChange, value } }) => (
            <FormControlLabel
              sx={{ mt: '16px' }}
              value={value}
              control={
                <Checkbox
                  checked={!!value}
                  disableRipple
                  data-testid={`${dataTestId}-AgeGated-Content`}
                  onChange={(e) => onChange(e.target.checked)}
                />
              }
              label={
                <Body2Typo ml={2}>
                  <b>{FIELD_NAMES[MESSAGE_CONTENT_FIELDS.AGE_GATED]}</b>:
                  Indicates whether messages include any age-gated content as
                  defined by Carrier and CTIA guidelines.
                </Body2Typo>
              }
            />
          )}
        />
      </Stack>
      <Divider />
      <SubtitleTypo mt={4}>
        Please provide at least two sample messages you will send your
        customers, and you can include up to five sample messages
        <IconButton
          aria-label="message-content-tooltip"
          onClick={() => setOpenModal(true)}
          data-testid={`${dataTestId}-Sample-Messages`}
        >
          <StyledIcon icon={Icons.info} />
        </IconButton>
      </SubtitleTypo>
      <SimpleDialog
        open={openModal}
        title="Sample messages"
        confirmText="Done"
        confirmButtonProps={{
          color: 'primary',
          onClick: () => setOpenModal(false),
          'data-testid': 'PlatformAdminUI-MessageContent-DoneButton',
        }}
      >
        <Body2Typo width="347px" mb={3}>
          Sample messages that your company will be sending. Please make sure
          there is an opt-out clause at the end of the message. It&apos;s a good
          idea to include Brand name in sample messages. If you use
          conversational messages between brand representatives and consumers,
          provide few examples.
        </Body2Typo>
      </SimpleDialog>
      <Stack spacing={8}>
        <Controller
          control={control}
          name="sampleMessageOne"
          rules={{ required: true, minLength: 20, maxLength: 1024 }}
          render={({ field: { onChange, onBlur, value = '' } }) => {
            return (
              <TextArea
                value={value}
                onChange={onChange}
                onBlur={onBlur}
                charLimit={1024}
                maxRows={5}
                required
                sx={{ width: '495px', mt: '16px' }}
                error={value && !!errors.sampleMessageOne}
                label={
                  <OverlineTypo>
                    {FIELD_NAMES[MESSAGE_CONTENT_FIELDS.SAMPLE_MESSAGE_ONE]}
                  </OverlineTypo>
                }
                placeholder="Example: Thank you for contacting [Brand Name]. We will get back to you as soon as possible. Reply STOP to opt-out."
                helperText="Minimum 20 characters"
                data-testid={`${dataTestId}-Sample1-Textarea`}
              />
            );
          }}
        />
        <Controller
          control={control}
          name="sampleMessageTwo"
          rules={{ required: true, minLength: 20, maxLength: 1024 }}
          render={({ field: { onChange, onBlur, value = '' } }) => {
            return (
              <TextArea
                value={value}
                onChange={onChange}
                onBlur={onBlur}
                charLimit={1024}
                maxRows={5}
                required
                sx={{ width: '495px', mt: '16px' }}
                error={value && !!errors.sampleMessageTwo}
                label={
                  <OverlineTypo>
                    {FIELD_NAMES[MESSAGE_CONTENT_FIELDS.SAMPLE_MESSAGE_TWO]}
                  </OverlineTypo>
                }
                placeholder="Example: It was nice talking with you the other day and thank you for reaching out. I'm excited to talk more about [Brand Name]. Reply STOP to opt-out."
                helperText="Minimum 20 characters"
                data-testid={`${dataTestId}-Sample2-Textarea`}
              />
            );
          }}
        />
        {Array(samples - 2)
          .fill(null)
          .map((_, index) => (
            <Controller
              control={control}
              name={`sampleMessage${index + 3}` as SampleTypes}
              key={index}
              rules={{ required: true, minLength: 20, maxLength: 1024 }}
              render={({ field: { onChange, onBlur, value = '' } }) => {
                return (
                  <TextArea
                    value={value}
                    onChange={onChange}
                    onBlur={onBlur}
                    charLimit={1024}
                    maxRows={5}
                    sx={{ width: '495px', mt: '16px' }}
                    error={value && !!errors[`sampleMessage${index + 3}`]}
                    placeholder="Type your message"
                    helperText="Minimum 20 characters"
                    data-testid={`${dataTestId}-MessageContent-Textarea`}
                    label={
                      <LabelWithDeleteButton
                        label={`Sample message ${index + 3}`}
                        sampleMessageId={`sampleMessage${index + 3}`}
                        onDelete={() =>
                          handleDeleteSampleMessage(
                            `sampleMessage${index + 3}` as SampleTypes,
                          )
                        }
                        dataTestId={`${dataTestId}-LabelWithDeleteButton`}
                      />
                    }
                  />
                );
              }}
            />
          ))}
      </Stack>
      <Stack direction="row" alignItems="center" my={5}>
        <Typography
          variant="subtitle3"
          sx={{ marginRight: '32px', color: '#A0A2AD' }}
        >
          Maximum 5 samples
        </Typography>
        <Typography
          variant="body2"
          fontWeight="bold"
          mr={2}
          sx={{ marginLeft: '69px', color: '#0B1029' }}
        >
          Added: {samples} out of 5
        </Typography>
        <Button
          variant="text"
          size="medium"
          onClick={() => samples < 5 && setSamples((sam) => sam + 1)}
          data-testid={`${dataTestId}-MessagingContent-Add`}
          disabled={samples === 5}
          sx={{ padding: '8px 16px' }}
        >
          <Icon size={16} icon={Icons.addCircle} sx={{ marginRight: '4px' }} />
          Add sample
        </Button>
      </Stack>
      <OverlineTypo mt={2}>
        Upload documents if you have more samples
      </OverlineTypo>
      <UploadComponent
        onFileFormats={setTypeModal}
        uploadedFiles={filesMMS}
        attachmentType="mms"
      />
      <FileTypesDialogMms open={typeModal} setOpen={setTypeModal} />
    </div>
  );
}
